import type { Conversation } from '@/features/conversation/models/conversation';
import { useConversations } from '@/features/conversation/queries/conversation.queries';
import { GradientAvatar } from '@/shared/components';
import { Box, Button, Typography } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Message } from '../../models/message';

interface ConversationListForwardProps {
  conversationId: string;
  searchQuery: string;
  sentConversations: Map<string, Message>;
  onConversationSend: (conversation: Conversation) => void;
  onConversationUndo: (conversationId: string) => void;
}

export const ConversationListForward: React.FC<ConversationListForwardProps> = ({
  conversationId,
  searchQuery,
  sentConversations,
  onConversationSend,
  onConversationUndo
}) => {
  const { t } = useTranslation();
  const { data: conversationsData, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useConversations();

  const conversations =
    conversationsData?.pages
      .flatMap((page) => page.conversations)
      .filter((conversation) => conversation.conversationId !== conversationId) || [];

  const handleSendClick = useCallback(
    (conversation: Conversation, event: React.MouseEvent) => {
      event.stopPropagation();
      onConversationSend(conversation);
    },
    [onConversationSend]
  );

  const handleUndoClick = useCallback(
    (conversationId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      onConversationUndo(conversationId);
    },
    [onConversationUndo]
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const getConversationAvatar = (conversation: Conversation) => {
    return (
      <GradientAvatar
        peerId={conversation.conversationId}
        variant={conversation.conversationType === 'GROUP' ? 'rounded' : 'circular'}
        name={conversation.conversationName}
        src={conversation.conversationAvatarRef}
      />
    );
  };

  if (isLoading) {
    return (
      <Box className='mex:p-4'>
        <Typography variant='body2' color='text.secondary'>
          {t('messages.forward.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className='mex:px-4 mex:py-3'>
      <Typography variant='subtitle1' className='mex:mb-3 mex:font-semibold mex:text-gray-800'>
        {t('messages.forward.label')}
      </Typography>

      {conversations.length === 0 ? (
        <Typography variant='body2' color='text.secondary' className='mex:py-8 mex:text-center'>
          {searchQuery ? t('messages.forward.noConversations') : t('messages.forward.emptyConversations')}
        </Typography>
      ) : (
        <Box className='mex:space-y-2'>
          {conversations.map((conversation) => {
            const isSent = sentConversations.has(conversation.conversationId!);
            return (
              <Box
                key={conversation.conversationId}
                className={`mex:flex mex:cursor-pointer mex:items-center mex:justify-between mex:rounded-lg mex:p-2 mex:transition-colors mex:hover:bg-gray-200 ${isSent ? 'mex:border mex:border-green-200 mex:bg-green-50' : ''}`}
              >
                <Box className='mex:flex mex:min-w-0 mex:flex-1 mex:items-center mex:space-x-3'>
                  {getConversationAvatar(conversation)}
                  <Box className='mex:min-w-0 mex:flex-1'>
                    <Typography variant='body1' className='mex:truncate mex:font-medium mex:text-gray-900'>
                      {conversation.conversationName || t('messages.forward.conversationFallback')}
                    </Typography>
                  </Box>
                </Box>

                {isSent ? (
                  <Button
                    variant='outlined'
                    size='small'
                    className='mex:!border-orange-300 mex:!text-orange-600 mex:!rounded-full mex:!px-3 mex:!py-1 mex:!min-w-0 mex:!text-xs mex:!font-medium mex:hover:!bg-orange-50'
                    onClick={(e) => handleUndoClick(conversation.conversationId!, e)}
                  >
                    {t('messages.forward.undoBtn')}
                  </Button>
                ) : (
                  <Button
                    variant='contained'
                    size='small'
                    className='mex:!bg-blue-500 mex:!text-white mex:!rounded-full mex:!px-3 mex:!py-1 mex:!min-w-0 mex:!text-xs mex:!font-medium'
                    onClick={(e) => handleSendClick(conversation, e)}
                  >
                    {t('messages.forward.sentBtn')}
                  </Button>
                )}
              </Box>
            );
          })}

          {hasNextPage && !isFetchingNextPage && (
            <Box className='mex:pt-2 mex:text-center'>
              <Button
                variant='text'
                size='small'
                onClick={handleLoadMore}
                className='mex:!text-blue-500 mex:hover:!text-blue-700'
              >
                {t('messages.forward.viewOriginal')}
              </Button>
            </Box>
          )}

          {isFetchingNextPage && (
            <Box className='mex:pt-2 mex:text-center'>
              <Typography variant='body2' className='mex:text-gray-500'>
                {t('messages.forward.loading')}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
